import { useState, useEffect, useMemo, useCallback } from 'react'
import styled from 'styled-components'
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    ReferenceLine,
    Legend,
} from 'recharts'
import { adminApi } from '../../../services/api'

interface TeamLoadEntry {
    week_start: string
    user_id: number
    display_name: string
    department: string | null
    user_type: string | null
    job_role: string | null
    avg_load_pct: number
    is_projected?: boolean
}

const RANGE_OPTIONS = [
    { label: '1M', months: 1 },
    { label: '3M', months: 3 },
    { label: '6M', months: 6 },
    { label: '1A', months: 12 },
]

// Palette for user lines
const COLORS = [
    '#1976D2', '#E53935', '#43A047', '#FB8C00', '#8E24AA',
    '#00ACC1', '#F4511E', '#3949AB', '#7CB342', '#C0CA33',
    '#D81B60', '#00897B', '#5E35B1', '#039BE5', '#FFB300',
    '#6D4C41', '#546E7A', '#EC407A', '#26A69A', '#AB47BC',
]

const Container = styled.div`
    background: white;
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`

const ChartHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    flex-wrap: wrap;
    gap: 8px;
`

const Title = styled.h3`
    margin: 0;
    font-size: 16px;
    color: #37474f;
`

const Controls = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
`

const RangeButtons = styled.div`
    display: flex;
    gap: 4px;
`

const RangeBtn = styled.button<{ $active: boolean }>`
    padding: 4px 10px;
    border: 1px solid ${({ $active }) => ($active ? '#1976D2' : '#ccc')};
    background: ${({ $active }) => ($active ? '#1976D2' : 'white')};
    color: ${({ $active }) => ($active ? 'white' : '#666')};
    border-radius: 4px;
    font-size: 12px;
    cursor: pointer;
`

const FilterSelect = styled.select`
    padding: 4px 8px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 12px;
`

const UserChips = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 8px;
    max-height: 80px;
    overflow-y: auto;
`

const Chip = styled.button<{ $active: boolean; $color: string }>`
    padding: 2px 8px;
    border: 1px solid ${({ $color }) => $color};
    background: ${({ $active, $color }) => ($active ? $color : 'white')};
    color: ${({ $active, $color }) => ($active ? 'white' : $color)};
    border-radius: 12px;
    font-size: 11px;
    cursor: pointer;
    white-space: nowrap;
    opacity: ${({ $active }) => ($active ? 1 : 0.5)};
`

const ChipActions = styled.div`
    display: flex;
    gap: 6px;
    margin-bottom: 6px;
`

const SmallBtn = styled.button`
    padding: 2px 8px;
    border: 1px solid #ccc;
    background: white;
    color: #666;
    border-radius: 4px;
    font-size: 11px;
    cursor: pointer;
`

const formatWeek = (dateStr: string) => {
    const d = new Date(dateStr)
    const day = d.getDate()
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
    return `${day} ${months[d.getMonth()]}`
}

type ViewMode = 'team_avg' | 'individual'

export default function TeamLoadChart() {
    const [data, setData] = useState<TeamLoadEntry[]>([])
    const [loading, setLoading] = useState(true)
    const [rangeMonths, setRangeMonths] = useState(6)
    const [viewMode, setViewMode] = useState<ViewMode>('team_avg')
    const [enabledUsers, setEnabledUsers] = useState<Set<number>>(new Set())
    const [filterType, setFilterType] = useState<string>('all')
    const [filterRole, setFilterRole] = useState<string>('all')

    const loadData = useCallback(async (months: number) => {
        setLoading(true)
        try {
            const endDate = new Date().toISOString().split('T')[0]
            const startDate = new Date(Date.now() - months * 30 * 86400000).toISOString().split('T')[0]
            const res = await adminApi.getTeamLoadHistory(startDate, endDate, true)
            setData(res.data || [])
        } catch (err) {
            console.error('Error loading team load history:', err)
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => { loadData(rangeMonths) }, [rangeMonths, loadData])

    // Available users with metadata
    const users = useMemo(() => {
        const map = new Map<number, { id: number; name: string; dept: string | null; type: string | null; jobRole: string | null }>()
        data.forEach((e) => map.set(e.user_id, { id: e.user_id, name: e.display_name, dept: e.department, type: e.user_type, jobRole: e.job_role }))
        return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name))
    }, [data])

    // Roles for filter
    const roles = useMemo(() => {
        const s = new Set<string>()
        users.forEach((u) => { if (u.jobRole) s.add(u.jobRole) })
        return Array.from(s).sort()
    }, [users])

    // User types for filter
    const userTypes = useMemo(() => {
        const s = new Set<string>()
        users.forEach((u) => { if (u.type) s.add(u.type) })
        return Array.from(s).sort()
    }, [users])

    // Initialize all users as enabled when data loads
    useEffect(() => {
        if (users.length > 0 && enabledUsers.size === 0) {
            setEnabledUsers(new Set(users.map((u) => u.id)))
        }
    }, [users])

    // Filtered users
    const filteredUsers = useMemo(() => {
        return users.filter((u) => {
            if (filterType !== 'all' && u.type !== filterType) return false
            if (filterRole !== 'all' && u.jobRole !== filterRole) return false
            return true
        })
    }, [users, filterType, filterRole])

    // Weeks (sorted)
    const weeks = useMemo(() => {
        const s = new Set<string>()
        data.forEach((e) => s.add(e.week_start))
        return Array.from(s).sort()
    }, [data])

    // Determine today's week for splitting historical/projected
    const todayStr = new Date().toISOString().split('T')[0]

    // Team average data with projection split
    const teamAvgData = useMemo(() => {
        const weekMapHistorical = new Map<string, { total: number; count: number }>()
        const weekMapProjected = new Map<string, { total: number; count: number }>()
        const activeUserIds = new Set(filteredUsers.filter((u) => enabledUsers.has(u.id)).map((u) => u.id))
        data.forEach((e) => {
            if (!activeUserIds.has(e.user_id)) return
            const map = e.is_projected ? weekMapProjected : weekMapHistorical
            const entry = map.get(e.week_start) || { total: 0, count: 0 }
            entry.total += e.avg_load_pct
            entry.count += 1
            map.set(e.week_start, entry)
        })
        return weeks.map((w) => {
            const hist = weekMapHistorical.get(w)
            const proj = weekMapProjected.get(w)
            const isFuture = w > todayStr
            const histValue = hist ? Math.round(hist.total / hist.count) : null
            const projValue = proj ? Math.round(proj.total / proj.count) : null
            return {
                week: formatWeek(w),
                load: histValue !== null ? histValue : (isFuture ? null : (projValue !== null ? projValue : null)),
                load_projected: projValue !== null ? projValue : (hist && w >= todayStr ? histValue : null),
            }
        })
    }, [data, weeks, filteredUsers, enabledUsers, todayStr])

    // Individual (multi-line) data: pivot to {week, user1: load, user2: load, ...}
    //
    // The backend returns a row per (user, week) with avg_load_pct=0 when the
    // user has no active assignments that week. Rendering those 0s as a flat
    // line stacks 10+ users on top of each other at y=0, which visually reads
    // as "lines disappearing / not continuous" between spikes. We drop 0s to
    // null here so connectNulls={true} on the <Line>s bridges directly between
    // the weeks that actually matter — same intent as the chart header says
    // ("Histórico de carga" — only the carga, not the idle periods).
    const individualData = useMemo(() => {
        const activeUsers = filteredUsers.filter((u) => enabledUsers.has(u.id))
        const userIndex = new Map<number, Map<string, number>>()
        const userProjIndex = new Map<number, Map<string, number>>()
        data.forEach((e) => {
            const idx = e.is_projected ? userProjIndex : userIndex
            if (!idx.has(e.user_id)) idx.set(e.user_id, new Map())
            idx.get(e.user_id)!.set(e.week_start, e.avg_load_pct)
        })
        const toDisplay = (v: number | undefined): number | null => {
            if (v == null) return null
            const r = Math.round(v)
            return r === 0 ? null : r   // 0% → null so lines bridge across idle weeks
        }
        return weeks.map((w) => {
            const row: Record<string, any> = { week: formatWeek(w) }
            activeUsers.forEach((u) => {
                const hist = userIndex.get(u.id)?.get(w)
                const proj = userProjIndex.get(u.id)?.get(w)
                const histRounded = toDisplay(hist)
                row[`u_${u.id}`] = histRounded
                row[`u_${u.id}_proj`] = toDisplay(proj) ?? (histRounded != null && w >= todayStr ? histRounded : null)
            })
            return row
        })
    }, [data, weeks, filteredUsers, enabledUsers, todayStr])

    const toggleUser = (id: number) => {
        setEnabledUsers((prev) => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    const selectAll = () => setEnabledUsers(new Set(filteredUsers.map((u) => u.id)))
    const selectNone = () => setEnabledUsers(new Set())

    const handleRange = (m: number) => setRangeMonths(m)

    if (loading) return null

    const activeFilteredUsers = filteredUsers.filter((u) => enabledUsers.has(u.id))

    return (
        <Container>
            <ChartHeader>
                <Title>Historico de carga semanal del equipo</Title>
                <Controls>
                    <RangeButtons>
                        {RANGE_OPTIONS.map((opt) => (
                            <RangeBtn key={opt.months} $active={rangeMonths === opt.months} onClick={() => handleRange(opt.months)}>
                                {opt.label}
                            </RangeBtn>
                        ))}
                    </RangeButtons>
                    <RangeBtn $active={viewMode === 'team_avg'} onClick={() => setViewMode('team_avg')}>
                        Media
                    </RangeBtn>
                    <RangeBtn $active={viewMode === 'individual'} onClick={() => setViewMode('individual')}>
                        Individual
                    </RangeBtn>
                    {roles.length > 1 && (
                        <FilterSelect value={filterRole} onChange={(e) => setFilterRole(e.target.value)}>
                            <option value="all">Todos roles</option>
                            {roles.map((r) => <option key={r} value={r}>{r}</option>)}
                        </FilterSelect>
                    )}
                    {userTypes.length > 1 && (
                        <FilterSelect value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                            <option value="all">Todos tipos</option>
                            {userTypes.map((t) => (
                                <option key={t} value={t}>{t === 'intern' ? 'Practicas' : t === 'regular' ? 'Regular' : t}</option>
                            ))}
                        </FilterSelect>
                    )}
                </Controls>
            </ChartHeader>

            {viewMode === 'individual' && (
                <>
                    <ChipActions>
                        <SmallBtn onClick={selectAll}>Todos</SmallBtn>
                        <SmallBtn onClick={selectNone}>Ninguno</SmallBtn>
                    </ChipActions>
                    <UserChips>
                        {filteredUsers.map((u, i) => (
                            <Chip
                                key={u.id}
                                $active={enabledUsers.has(u.id)}
                                $color={COLORS[i % COLORS.length]}
                                onClick={() => toggleUser(u.id)}
                            >
                                {u.name}
                            </Chip>
                        ))}
                    </UserChips>
                </>
            )}

            <ResponsiveContainer width="100%" height={320}>
                {viewMode === 'team_avg' ? (
                    <LineChart data={teamAvgData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="week" tick={{ fontSize: 10 }} interval={Math.max(0, Math.floor(weeks.length / 10) - 1)} />
                        <YAxis tick={{ fontSize: 11 }} unit="%" />
                        <Tooltip formatter={(value: any, name: any) => [value != null ? `${value}%` : '-', name === 'load_projected' ? 'Proyección' : 'Media equipo']} contentStyle={{ fontSize: 12 }} />
                        <ReferenceLine y={100} stroke="#F44336" strokeDasharray="4 4" />
                        <Line type="monotone" dataKey="load" stroke="#1976D2" strokeWidth={2} dot={weeks.length <= 13} connectNulls={true} name="Media equipo" />
                        <Line type="monotone" dataKey="load_projected" stroke="#1976D2" strokeWidth={2} strokeDasharray="8 4" dot={false} connectNulls={true} name="Proyección" />
                    </LineChart>
                ) : (
                    <LineChart data={individualData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="week" tick={{ fontSize: 10 }} interval={Math.max(0, Math.floor(weeks.length / 10) - 1)} />
                        <YAxis tick={{ fontSize: 11 }} unit="%" />
                        <Tooltip contentStyle={{ fontSize: 11, maxHeight: 300, overflowY: 'auto' }} />
                        <ReferenceLine y={100} stroke="#F44336" strokeDasharray="4 4" />
                        {activeFilteredUsers.map((u, i) => {
                            const color = COLORS[filteredUsers.indexOf(u) % COLORS.length]
                            return [
                                <Line
                                    key={`${u.id}-hist`}
                                    type="monotone"
                                    dataKey={`u_${u.id}`}
                                    name={u.name}
                                    stroke={color}
                                    strokeWidth={1.5}
                                    dot={false}
                                    connectNulls={true}
                                />,
                                <Line
                                    key={`${u.id}-proj`}
                                    type="monotone"
                                    dataKey={`u_${u.id}_proj`}
                                    name={`${u.name} (proj)`}
                                    stroke={color}
                                    strokeWidth={1.5}
                                    strokeDasharray="8 4"
                                    dot={false}
                                    connectNulls={true}
                                    legendType="none"
                                />,
                            ]
                        })}
                    </LineChart>
                )}
            </ResponsiveContainer>
        </Container>
    )
}
